import {
  Control,
  FieldPath,
  FieldValues,
  UseFormRegister,
} from "react-hook-form";

import SignUpFormLayout from "~/common/components/signUp/SignUpFormLayout";
import SignUpProgress from "~/common/components/signUp/SignUpProgress";
import Input from "~/common/components/ui/Input";
import Select from "~/common/components/ui/select/Select";
import { DIRECTIONS } from "~/common/constants";
import { usaStatesOptions } from "~/common/constants/usaStates";
import { formatPhoneNumberField } from "~/common/utils";
import {
  PharmacyFormValuesType,
  SignUpPatientSchemaType,
} from "~/patients/utils";
import { FormErrors } from "~/patients/utils/types/forms";

interface DefaultPharmacyFormProps<T extends FieldValues> {
  handleGoBack: () => void;
  totalSteps: number;
  register: UseFormRegister<SignUpPatientSchemaType>;
  onNext: () => Promise<void>;
  step: number;
  pharmacyStateKey: FieldPath<T>;
  errors?: FormErrors<PharmacyFormValuesType>;
  control?: Control<T>;
}

export const DefaultPharmacyForm = <T extends FieldValues>({
  handleGoBack,
  totalSteps,
  onNext,
  register,
  step,
  pharmacyStateKey,
  errors,
  control,
}: DefaultPharmacyFormProps<T>) => {
  return (
    <>
      <SignUpProgress step={step} totalSteps={totalSteps} />
      <SignUpFormLayout
        title="Add your default pharmacy"
        description="You’ll be able to add/modify your pharmacies in the settings page. "
        handleGoBack={handleGoBack}
        handleData={onNext}
      >
        <Input
          id="name"
          label="Pharmacy name"
          {...register("pharmacy.name")}
          error={errors?.name?.message}
        />

        <Input
          id="phoneNumber"
          label="Phone number"
          {...register("pharmacy.phone_number")}
          {...formatPhoneNumberField<SignUpPatientSchemaType>(
            "pharmacy.phone_number",
            register,
          )}
          error={errors?.phone_number?.message}
        />

        <Input
          id="street"
          label="Street address"
          {...register("pharmacy.address.street")}
          error={errors?.address?.street?.message}
        />

        <div className="flex flex-col gap-4 md:flex-row md:gap-2">
          <Input
            id="city"
            label="City"
            {...register("pharmacy.address.city")}
            error={errors?.address?.city?.message}
          />

          <Select
            error={errors?.address?.state?.message}
            direction={DIRECTIONS.top}
            label="State"
            placeholder={"Select State"}
            id={pharmacyStateKey}
            options={usaStatesOptions}
            control={control}
            containerClassName="gap-2"
          />

          <Input
            id="zipcode"
            label="ZIP code"
            {...register("pharmacy.address.zipcode")}
            error={errors?.address?.zipcode?.message}
          />
        </div>
      </SignUpFormLayout>
    </>
  );
};
